import React, { useEffect, useMemo, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import RefreshIcon from '@material-ui/icons/Refresh'
import AddIcon from '@material-ui/icons/Add'

import Widget from '../../../components/Widget'
import {
    grantHandlerMembership,
    revokeHandlerMembership,
    useAppointmentHandlers,
    useHandlerCandidates,
    useHandlerMemberships,
} from '../../../apis'
import { useApiHttpState } from '../../../context/ApiContext'
import {
    DestructiveConfirmationDialog,
    SectionStateBoundary,
    createIconActionName,
    normalizeSectionError,
    useSnackbarSectionInteraction,
} from '../primitives'

export const REVOCATION_CONSEQUENCE =
    'Existing appointment assignments will remain assigned and the user will be unavailable for new assignments.'
export const GRANT_CONSEQUENCE =
    'The user will become available for new appointment assignments. Their account and other roles will not be changed.'

const text = value => String(value === undefined || value === null ? '' : value)
const normalizedText = value => text(value).trim().toLocaleLowerCase()

export const compareSafeUsers = (left, right) => {
    for (const field of ['lastName', 'firstName', 'email']) {
        const comparison = normalizedText(left[field]).localeCompare(normalizedText(right[field]))
        if (comparison) return comparison
    }
    return Number(left.id) - Number(right.id)
}

export const filterMembers = (members, query) => {
    const normalizedQuery = normalizedText(query)
    const ordered = [...(Array.isArray(members) ? members : [])].sort(compareSafeUsers)
    if (!normalizedQuery) return ordered
    return ordered.filter(member =>
        ['firstName', 'lastName', 'email'].some(field =>
            normalizedText(member[field]).includes(normalizedQuery),
        ),
    )
}
export const safeUserLabel = user => {
    const name = [text(user.firstName).trim(), text(user.lastName).trim()].filter(Boolean).join(' ')
    const email = text(user.email).trim()
    return name ? `${name} (${email || `user ${user.id}`})` : email || `User ${user.id}`
}

const isResultTuple = result => Array.isArray(result) && result.length === 2 && (
    result[0] === null ||
    result[1] === null ||
    result[0] instanceof Error ||
    !!(result[0] && result[0].response)
)
const resultError = result => (isResultTuple(result) ? result[0] : null)
const resultPayload = result => {
    const response = isResultTuple(result) ? result[1] : result
    return response && Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response
}

export const refreshHandlerDependencies = async (
    revalidateMemberships,
    revalidateCandidates,
    revalidateSelectableHandlers,
) => {
    const results = await Promise.all([
        revalidateMemberships(),
        revalidateCandidates(),
        revalidateSelectableHandlers(),
    ])
    const failure = results.map(resultError).find(Boolean)
    if (failure) throw failure
    return resultPayload(results[0])
}

const SafeUserCells = ({ user }) => (
    <React.Fragment>
        <TableCell>{user.id}</TableCell>
        <TableCell>{text(user.firstName).trim() || '—'}</TableCell>
        <TableCell>{text(user.lastName).trim() || '—'}</TableCell>
        <TableCell>{text(user.email).trim()}</TableCell>
        <TableCell>{user.active ? 'Active' : 'Inactive'}</TableCell>
    </React.Fragment>
)

const UserTableHead = ({ actionLabel }) => (
    <TableHead>
        <TableRow>
            <TableCell component="th" scope="col">User ID</TableCell>
            <TableCell component="th" scope="col">First name</TableCell>
            <TableCell component="th" scope="col">Last name</TableCell>
            <TableCell component="th" scope="col">Email</TableCell>
            <TableCell component="th" scope="col">Status</TableCell>
            <TableCell component="th" scope="col">{actionLabel}</TableCell>
        </TableRow>
    </TableHead>
)

const CandidateSearch = ({
    open,
    query,
    onQueryChange,
    candidatesState,
    selectedCandidate,
    onSelect,
    onCancelSelection,
    onConfirm,
    onClose,
    pending,
    canSubmit,
    failure,
}) => {
    const titleId = 'handler-membership-grant-title'
    const consequenceId = 'handler-membership-grant-consequence'
    const candidates = useMemo(
        () => [...(candidatesState.candidates || [])].sort(compareSafeUsers),
        [candidatesState.candidates],
    )
    const searching = candidatesState.isLoading || candidatesState.debouncedQuery !== query.trim()
    return (
        <Dialog
            open={open}
            onClose={pending ? undefined : onClose}
            disableBackdropClick
            disableEscapeKeyDown={pending}
            aria-labelledby={titleId}
            aria-describedby={selectedCandidate ? consequenceId : undefined}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id={titleId}>Grant Appointment Handler role</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    id="handler-candidate-search"
                    label="Search candidate users"
                    value={query}
                    onChange={event => onQueryChange(event.target.value)}
                    disabled={pending}
                    margin="normal"
                />
                {searching && (
                    <div role="status" aria-live="polite">
                        <CircularProgress size={18} aria-hidden="true" /> Searching candidate users.
                    </div>
                )}
                {!searching && candidatesState.isError && (
                    <div role="alert">
                        Candidate users could not be loaded.
                        <Button type="button" onClick={candidatesState.revalidateCandidates}>
                            Retry candidate search
                        </Button>
                    </div>
                )}
                {!searching && !candidatesState.isError && candidates.length === 0 && (
                    <p>No candidate users match this search.</p>
                )}
                {!searching && !candidatesState.isError && candidates.length > 0 && (
                    <Table size="small" aria-label="Candidate users">
                        <UserTableHead actionLabel="Grant role" />
                        <TableBody>
                            {candidates.map(candidate => (
                                <TableRow key={candidate.id}>
                                    <SafeUserCells user={candidate} />
                                    <TableCell>
                                        <Button
                                            type="button"
                                            onClick={() => onSelect(candidate)}
                                            disabled={pending || !canSubmit}
                                            aria-label={createIconActionName(
                                                'Grant Appointment Handler role to',
                                                safeUserLabel(candidate),
                                            )}
                                        >
                                            Grant role
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {!!selectedCandidate && (
                    <div>
                        <DialogContentText id={consequenceId}>
                            <strong>{safeUserLabel(selectedCandidate)}</strong>
                            {' — '}
                            {GRANT_CONSEQUENCE}
                        </DialogContentText>
                        {!!failure && <div role="alert">{failure.message}</div>}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={onClose} disabled={pending}>Close</Button>
                {!!selectedCandidate && (
                    <Button type="button" onClick={onCancelSelection} disabled={pending}>
                        Choose another user
                    </Button>
                )}
                {!!selectedCandidate && (
                    <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        onClick={onConfirm}
                        disabled={pending || !canSubmit}
                        aria-describedby={consequenceId}
                    >
                        Confirm grant
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
export default function HandlerMembershipSection() {
    const { api } = useApiHttpState()
    const memberships = useHandlerMemberships()
    const selectableHandlers = useAppointmentHandlers()
    const interaction = useSnackbarSectionInteraction()
    const [memberQuery, setMemberQuery] = useState('')
    const [grantOpen, setGrantOpen] = useState(false)
    const [candidateQuery, setCandidateQuery] = useState('')
    const [selectedCandidate, setSelectedCandidate] = useState(null)
    const [grantFailure, setGrantFailure] = useState(null)
    const [revokeMember, setRevokeMember] = useState(null)
    const sectionHeadingRef = useRef(null)
    const revokeOpenerRef = useRef(null)
    const bootstrapAttemptRef = useRef(null)
    // Candidate data is needed only while the grant dialog is open. Membership
    // mutations still explicitly revalidate this cache before they unlock.
    const candidatesState = useHandlerCandidates(candidateQuery, { enabled: grantOpen })

    useEffect(() => {
        if (interaction.state.confirmedData || memberships.isLoading) return
        const attemptKey = memberships.isError
            ? `error:${memberships.isError.message || 'load'}`
            : memberships.versionToken || 'missing'
        if (bootstrapAttemptRef.current === attemptKey) return
        bootstrapAttemptRef.current = attemptKey
        interaction.load(() => {
            if (memberships.isError) throw memberships.isError
            return memberships.managementSection
        })
    }, [
        interaction,
        memberships.isError,
        memberships.isLoading,
        memberships.managementSection,
        memberships.versionToken,
    ])

    const coordinatedRefresh = () => refreshHandlerDependencies(
        memberships.revalidateMemberships,
        candidatesState.revalidateCandidates,
        selectableHandlers.revalidateSelectableHandlers,
    )

    const retryLoad = () => interaction.load(async () => {
        const result = await memberships.revalidateMemberships()
        const failure = resultError(result)
        if (failure) throw failure
        return resultPayload(result)
    })
    const refresh = () => interaction.refresh(coordinatedRefresh)

    const confirmedMembers = interaction.state.confirmedData
        ? interaction.state.confirmedData.records
        : []
    const visibleMembers = useMemo(
        () => filterMembers(confirmedMembers, memberQuery),
        [confirmedMembers, memberQuery],
    )
    const versionToken = interaction.state.confirmedData &&
        interaction.state.confirmedData.versionToken

    const submitGrant = async () => {
        if (!selectedCandidate) return
        setGrantFailure(null)
        const result = await interaction.submit({
            mutation: () => grantHandlerMembership(api, selectedCandidate.id, versionToken),
            refresh: coordinatedRefresh,
            draft: { candidateQuery, userId: selectedCandidate.id },
            mutationKey: `grant-${selectedCandidate.id}`,
            successMessage: `${safeUserLabel(selectedCandidate)} is now an Appointment Handler.`,
            retainDraft: true,
        })
        if (!result.ok) {
            setGrantFailure(normalizeSectionError(result.error))
            return
        }
        setGrantOpen(false)
        setSelectedCandidate(null)
        setCandidateQuery('')
    }

    const submitRevoke = () => interaction.submit({
        mutation: () => revokeHandlerMembership(api, revokeMember.id, versionToken),
        refresh: coordinatedRefresh,
        draft: { userId: revokeMember.id },
        mutationKey: `revoke-${revokeMember.id}`,
        successMessage: `${safeUserLabel(revokeMember)} is no longer an Appointment Handler.`,
    })
    return (
        <section aria-labelledby="handler-memberships-heading">
            <Widget
                disableWidgetMenu
                header={
                    <React.Fragment>
                        <Typography
                            id="handler-memberships-heading"
                            ref={sectionHeadingRef}
                            component="h2"
                            variant="h5"
                            color="textSecondary"
                            tabIndex={-1}
                        >
                            Handler Memberships
                        </Typography>
                        <div>
                            <IconButton
                                type="button"
                                aria-label="Refresh Handler Memberships"
                                onClick={refresh}
                                disabled={interaction.isPending || !interaction.state.confirmedData}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                type="button"
                                color="primary"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setGrantFailure(null)
                                    setGrantOpen(true)
                                }}
                                disabled={!interaction.canSubmit}
                            >
                                Grant Appointment Handler role
                            </Button>
                        </div>
                    </React.Fragment>
                }
            >
                <SectionStateBoundary
                    state={interaction.state}
                    sectionName="Handler Memberships"
                    emptyMessage="No users have the Appointment Handler role."
                    onRetryLoad={retryLoad}
                    onRetryRefresh={refresh}
                    onAcceptStale={interaction.acceptStale}
                >
                    {() => (
                        <React.Fragment>
                            <TextField
                                id="handler-member-search"
                                label="Search current handlers"
                                value={memberQuery}
                                onChange={event => setMemberQuery(event.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            {visibleMembers.length === 0 ? (
                                <p>No current handlers match this search.</p>
                            ) : (
                                <Table size="small" aria-label="Appointment Handler members">
                                    <UserTableHead actionLabel="Revoke role" />
                                    <TableBody>
                                        {visibleMembers.map(member => (
                                            <TableRow key={member.id}>
                                                <SafeUserCells user={member} />
                                                <TableCell>
                                                    <Button
                                                        type="button"
                                                        onClick={event => {
                                                            revokeOpenerRef.current = event.currentTarget
                                                            setRevokeMember(member)
                                                        }}
                                                        disabled={!interaction.canSubmit}
                                                        aria-label={createIconActionName(
                                                            'Revoke Appointment Handler role from',
                                                            safeUserLabel(member),
                                                        )}
                                                    >
                                                        Revoke role
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </React.Fragment>
                    )}
                </SectionStateBoundary>
            </Widget>
            <CandidateSearch
                open={grantOpen}
                query={candidateQuery}
                onQueryChange={value => {
                    setCandidateQuery(value)
                    setSelectedCandidate(null)
                    setGrantFailure(null)
                }}
                candidatesState={candidatesState}
                selectedCandidate={selectedCandidate}
                onSelect={candidate => {
                    setSelectedCandidate(candidate)
                    setGrantFailure(null)
                }}
                onCancelSelection={() => {
                    setSelectedCandidate(null)
                    setGrantFailure(null)
                }}
                onConfirm={submitGrant}
                onClose={() => {
                    if (interaction.isPending) return
                    setGrantOpen(false)
                    setSelectedCandidate(null)
                    setGrantFailure(null)
                }}
                pending={interaction.isPending}
                canSubmit={interaction.canSubmit}
                failure={grantFailure}
            />
            <DestructiveConfirmationDialog
                open={!!revokeMember}
                title="Revoke Appointment Handler role"
                recordLabel={revokeMember ? safeUserLabel(revokeMember) : ''}
                consequence={REVOCATION_CONSEQUENCE}
                confirmLabel="Confirm revocation"
                onConfirm={submitRevoke}
                onClose={() => setRevokeMember(null)}
                onConfirmed={() => setRevokeMember(null)}
                openerRef={revokeOpenerRef}
                sectionHeadingRef={sectionHeadingRef}
            />
        </section>
    )
}
